import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { User, PuntoVendita } from '../types';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Pencil, IdCard, Archive, CheckCircle2, Trash2, Check, X, RefreshCw, Download } from 'lucide-react';
import { useToast, useConfirm } from '../components/ui';
import './UtentiPage.css';

interface UserFormData {
  nome: string;
  postazione: boolean;
  cognome: string;
  email: string;
  telefono: string;
  codice_fiscale: string;
  matricola: string;
  data_nascita: string;
  data_assunzione: string;
  data_cessazione: string;
  password: string;
  ruolo: 'banconista' | 'produzione' | 'manager' | 'admin';
  punto_vendita_id: number | null;
  attivo: boolean;
  costo_orario: string;
}

const UtentiPage: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [utenti, setUtenti] = useState<User[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('');
  const [filterAttivo, setFilterAttivo] = useState('tutti');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    postazione: false,
    cognome: '',
    telefono: '',
    codice_fiscale: '',
    matricola: '',
    data_nascita: '',
    data_assunzione: '',
    data_cessazione: '',

    password: '',
    ruolo: 'banconista',
    punto_vendita_id: null,
    attivo: true,
    costo_orario: '0.00'
  });

  // State per il modal del badge QR
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeUser, setBadgeUser] = useState<User | null>(null);
  const [badgeQrCodeUrl, setBadgeQrCodeUrl] = useState<string | null>(null);
  const [badgeLoading, setBadgeLoading] = useState(false);

  // Carica dati all'avvio
  useEffect(() => {
    loadUtenti();
    loadPuntiVendita();
  }, []);

  const loadUtenti = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      setUtenti(response);
      setError(null);
    } catch (err: any) {
      console.error('Errore caricamento utenti:', err);
      setError(err.message || 'Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const loadPuntiVendita = async () => {
    try {
      const response = await apiService.getPuntiVendita();
      setPuntiVendita(response);
    } catch (err: any) {
      console.error('Errore caricamento punti vendita:', err);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      nome: '',
      email: '',
      password: '',
      ruolo: 'banconista',
      punto_vendita_id: puntiVendita.length > 0 ? puntiVendita[0].id : null,
      attivo: true,
      costo_orario: '0.00',
      postazione: false,
      cognome: '',
      telefono: '',
      codice_fiscale: '',
      matricola: '',
      data_nascita: '',
      data_assunzione: '',
      data_cessazione: ''
    });
    setShowModal(true);
  };

  const handleEditUser = (utente: User) => {
    setEditingUser(utente);
    setFormData({
      nome: utente.nome,
      email: utente.email,
      password: '', // Non mostriamo la password esistente
      ruolo: utente.ruolo,
      punto_vendita_id: utente.punto_vendita_id || null,
      attivo: utente.attivo,
      costo_orario: String(utente.costo_orario || '0.00'),
      postazione: utente.postazione === true,
      cognome: utente.cognome || '',
      telefono: utente.telefono || '',
      codice_fiscale: utente.codice_fiscale || '',
      matricola: utente.matricola || '',
      // Le date arrivano come timestamp ISO ma l'input vuole il solo giorno
      data_nascita: (utente.data_nascita || '').slice(0, 10),
      data_assunzione: (utente.data_assunzione || '').slice(0, 10),
      data_cessazione: (utente.data_cessazione || '').slice(0, 10)
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Modifica utente esistente
        const updateData: any = {
          nome: formData.nome,
          email: formData.email,
          ruolo: formData.ruolo,
          punto_vendita_id: formData.punto_vendita_id,
          attivo: formData.attivo,
          costo_orario: Number(formData.costo_orario) || 0,
          postazione: formData.postazione,
          cognome: formData.cognome,
          telefono: formData.telefono,
          codice_fiscale: formData.codice_fiscale,
          matricola: formData.matricola,
          data_nascita: formData.data_nascita,
          data_assunzione: formData.data_assunzione,
          data_cessazione: formData.data_cessazione
        };
        
        // Includi password solo se è stata inserita
        if (formData.password.trim()) {
          updateData.password = formData.password;
        }
        
        await apiService.updateUser(editingUser.id, updateData);
      } else {
        // Crea nuovo utente
        if (!formData.password.trim()) {
          setError('La password è richiesta per i nuovi utenti');
          return;
        }
        
        await apiService.createUser({
          nome: formData.nome,
          email: formData.email,
          password: formData.password,
          ruolo: formData.ruolo,
          punto_vendita_id: formData.punto_vendita_id,
          attivo: formData.attivo,
          costo_orario: Number(formData.costo_orario) || 0,
          postazione: formData.postazione
        } as any);
      }
      
      setShowModal(false);
      setError(null);
      await loadUtenti(); // Ricarica la lista
    } catch (err: any) {
      console.error('Errore salvataggio utente:', err);
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleToggleAttivo = async (utente: User) => {
    if (utente.id === user?.id) {
      setError('Non puoi disattivare il tuo account');
      return;
    }
    
    try {
      await apiService.updateUser(utente.id, { attivo: !utente.attivo });
      await loadUtenti();
      setError(null);
    } catch (err: any) {
      console.error('Errore toggle attivo:', err);
      setError(err.message || 'Errore nell\'aggiornamento');
    }
  };

  const handleDeleteUser = async (utente: User) => {
    if (utente.id === user?.id) {
      setError('Non puoi eliminare il tuo account');
      return;
    }
    
    const ok = await confirm({
      title: 'Archivia utente',
      message: `Vuoi archiviare l'utente ${utente.nome}?`,
      variant: 'danger',
      confirmLabel: 'Archivia'
    });
    if (!ok) {
      return;
    }

    try {
      await apiService.deleteUser(utente.id);
      setUtenti(prevUtenti =>
        prevUtenti.filter(u => u.id !== utente.id)
      );
      setError(null);
      toast.success('Utente archiviato');
    } catch (err: any) {
      console.error('Errore eliminazione utente:', err);
      setError(err.message || 'Errore nell\'eliminazione');
    }
  };

  const handleShowBadge = async (utente: User) => {
    setBadgeUser(utente);
    setBadgeQrCodeUrl(null);
    setBadgeLoading(true);
    setShowBadgeModal(true);
    
    try {
      const response = await apiService.getUserQrToken(utente.id);
      setBadgeQrCodeUrl(response.qrCodeUrl || null);
    } catch (err: any) {
      console.error('Errore caricamento token QR:', err);
      setError('Impossibile caricare il codice QR dell\'utente');
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleRegenerateBadgeQr = async () => {
    if (!badgeUser) return;
    
    const ok = await confirm({
      title: 'Rigenera codice QR',
      message: `Vuoi rigenerare il codice QR per l'utente ${badgeUser.nome}? Il codice precedente non sarà più valido.`,
      variant: 'danger',
      confirmLabel: 'Rigenera'
    });
    if (!ok) {
      return;
    }


    setBadgeLoading(true);
    try {
      const response = await apiService.regenerateUserQrToken(badgeUser.id);
      setBadgeQrCodeUrl(response.qrCodeUrl || null);
      setError(null);
      toast.success('Codice QR rigenerato');
    } catch (err: any) {
      console.error('Errore rigenerazione token QR:', err);
      setError('Impossibile rigenerare il codice QR dell\'utente');
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleDownloadBadgePdf = async () => {
    if (!badgeUser) return;
    
    const badgeElement = document.getElementById('operator-badge-card');
    if (!badgeElement) {
      setError('Elemento badge non trovato per il download');
      return;
    }
    
    setBadgeLoading(true);
    try {
      const canvas = await html2canvas(badgeElement, {
        scale: 3, // Alta qualità
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Formato badge standard (85mm x 120mm)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [85, 120]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, 85, 120);
      pdf.save(`badge-${badgeUser.nome.toLowerCase().replace(/\s+/g, '-')}.pdf`);
      setError(null);
    } catch (err: any) {
      console.error('Errore durante la generazione del PDF:', err);
      setError('Errore durante il download del badge PDF');
    } finally {
      setBadgeLoading(false);
    }
  };

  // Filtri
  const filteredUtenti = utenti.filter(utente => {
    const matchesSearch = utente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         utente.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRuolo = filterRuolo === '' || utente.ruolo === filterRuolo;
    const matchesAttivo = filterAttivo === 'tutti' || 
                         (filterAttivo === 'attivi' && utente.attivo) ||
                         (filterAttivo === 'archiviati' && !utente.attivo);
    
    return matchesSearch && matchesRuolo && matchesAttivo;
  });

  const getRuoloBadgeClass = (ruolo: string) => {
    switch (ruolo) {
      case 'admin': return 'badge-admin';
      case 'manager': return 'badge-manager';
      case 'produzione': return 'badge-produzione';
      case 'banconista': return 'badge-banconista';
      default: return 'badge-default';
    }
  };

  if (user?.ruolo !== 'admin') {
    return (
      <div className="utenti-page">
        <div className="access-denied">
          <h2>Accesso Negato</h2>
          <p>Solo gli amministratori possono gestire gli utenti.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento utenti...</p>
      </div>
    );
  }

  return (
    <div className="utenti-page">
      <div className="page-header">
        <h1>Gestione Utenti</h1>
        <p>Gestisci utenti, ruoli e punti vendita assegnati</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Controlli superiori */}
      <div className="page-controls">
        <div className="search-filters">
          <input
            type="text"
            placeholder="Cerca per nome o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select
            value={filterRuolo}
            onChange={(e) => setFilterRuolo(e.target.value)}
            className="filter-select"
          >
            <option value="">Tutti i ruoli</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="produzione">Produzione</option>
            <option value="banconista">Banconista</option>
          </select>
          
          <select
            value={filterAttivo}
            onChange={(e) => setFilterAttivo(e.target.value)}
            className="filter-select"
          >
            <option value="tutti">Tutti gli stati</option>
            <option value="attivi">Solo attivi</option>
            <option value="archiviati">Solo archiviati</option>
          </select>
        </div>

        {user?.ruolo === 'admin' && (
          <button 
            className="btn btn-primary"
            onClick={handleCreateUser}
          >
            + Nuovo Utente
          </button>
        )}
      </div>

      {/* Lista utenti */}
      <div className="utenti-grid">
        {filteredUtenti.map(utente => (
          <div key={utente.id} className={`utente-card ${!utente.attivo ? 'archiviato' : ''}`}>
            <div className="utente-header">
              <div className="utente-info">
                <h3>{[utente.nome, utente.cognome].filter(Boolean).join(' ')}</h3>
                <p className="email">{utente.email}</p>
              </div>
              <div className="utente-badges">
                <span className={`badge ${getRuoloBadgeClass(utente.ruolo)}`}>
                  {utente.ruolo}
                </span>
                <span className={`status ${utente.attivo ? 'attivo' : 'archiviato'}`}>
                  {utente.attivo ? 'Attivo' : 'Archiviato'}
                </span>
              </div>
            </div>
            
            <div className="utente-details">
              <div className="detail-row">
                <span className="label">Punto vendita:</span>
                <span className="value">
                  {utente.punto_vendita_nome || 'Non assegnato'}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Costo orario:</span>
                <span className="value">
                  {utente.costo_orario ? `€${Number(utente.costo_orario).toFixed(2)}/h` : '€0.00/h'}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Codice fiscale:</span>
                <span className="value">{utente.codice_fiscale || 'da inserire'}</span>
              </div>
              {utente.telefono && (
                <div className="detail-row">
                  <span className="label">Cellulare:</span>
                  <span className="value">{utente.telefono}</span>
                </div>
              )}
              {utente.data_assunzione && (
                <div className="detail-row">
                  <span className="label">Assunto il:</span>
                  <span className="value">
                    {new Date(utente.data_assunzione).toLocaleDateString('it-IT')}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="label">Creato il:</span>
                <span className="value">
                  {new Date(utente.created_at).toLocaleDateString('it-IT')}
                </span>
              </div>
            </div>
            
            {user?.ruolo === 'admin' && (
              <div className="utente-actions">
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleEditUser(utente)}
                >
                  <Pencil size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Modifica
                </button>
                <button
                  className="btn btn-sm"
                  onClick={() => handleShowBadge(utente)}
                  style={{ backgroundColor: '#17a2b8', color: 'white', borderColor: '#17a2b8' }}
                >
                  <IdCard size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Badge QR
                </button>
                <button
                  className={`btn btn-sm ${utente.attivo ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleToggleAttivo(utente)}
                  disabled={utente.id === user?.id}
                >
                  {utente.attivo
                    ? <><Archive size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Archivia</>
                    : <><CheckCircle2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Riattiva</>}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteUser(utente)}
                  disabled={utente.id === user?.id}
                >
                  <Trash2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Elimina
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredUtenti.length === 0 && (
        <div className="empty-state">
          <p>Nessun utente trovato con i filtri selezionati.</p>
        </div>
      )}

      {/* Box informativo sui ruoli */}
      <div className="roles-info-box">
        <h3>Guida ai Ruoli</h3>
        <div className="roles-grid">
          {[
            {
              badge: 'badge-admin',
              sigla: 'Admin',
              nome: 'Amministratore',
              si: [
                'Accesso completo al sistema',
                'Gestione utenti, gusti, ingredienti e punti vendita',
                'Analytics, previsioni e controllo di gestione',
                'Archivio personale, buste paga e timbrature',
                'Configurazione sistema e integrazioni',
              ],
              no: [],
            },
            {
              badge: 'badge-manager',
              sigla: 'Manager',
              nome: 'Manager',
              si: [
                'Produzione, giacenza, smaltimento e storico',
                'Incasso del giorno, ticket e versamento cassa',
                'Turni e propri documenti',
              ],
              no: [
                'Analytics, previsioni e controllo di gestione',
                'Archivio personale e timbrature',
                'Gestione utenti e configurazione sistema',
              ],
            },
            {
              badge: 'badge-produzione',
              sigla: 'Produzione',
              nome: 'Produzione',
              si: [
                'Registrazione produzione, giacenza e smaltimento',
                'Storico e calendario produzione',
                'Turni e propri documenti',
              ],
              no: [
                'Cassa e incassi',
                'Gestione gusti, ingredienti e utenti',
              ],
            },
            {
              badge: 'badge-banconista',
              sigla: 'Banconista',
              nome: 'Banconista',
              si: [
                'Incasso del giorno (contanti)',
                'Rilevazione giacenza',
                'Turni e propri documenti',
              ],
              no: [
                'Registrazione produzione e smaltimento',
                'Versamenti e gestione cassa',
                'Gestione e configurazione',
              ],
            },
          ].map((ruolo) => (
            <div className="role-card" key={ruolo.sigla}>
              <div className="role-header">
                <span className={`badge ${ruolo.badge}`}>{ruolo.sigla}</span>
                <h4>{ruolo.nome}</h4>
              </div>
              <ul className="role-permissions">
                {ruolo.si.map((voce) => (
                  <li key={voce}>
                    <Check size={14} color="var(--success)" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    {voce}
                  </li>
                ))}
                {ruolo.no.map((voce) => (
                  <li key={voce}>
                    <X size={14} color="var(--error)" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    {voce}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <div className="roles-note">
          <p>
            <strong>Suggerimento:</strong> assegna sempre il ruolo minimo necessario.
            Produzione e banconista coprono l'operatività quotidiana, il manager supervisiona
            laboratorio e cassa, l'admin ha in più analisi, anagrafiche e configurazione.
            Ogni dipendente, con qualunque ruolo, vede le proprie buste paga dalla dashboard.
          </p>
        </div>
      </div>

      {/* Modal per creazione/modifica */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingUser ? 'Modifica Utente' : 'Nuovo Utente'}</h2>
              <button 
                className="btn-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Nome"
                />
              </div>

              <div className="form-group">
                <label>Cognome</label>
                <input
                  type="text"
                  value={formData.cognome}
                  onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                  placeholder="Cognome"
                />
              </div>

              <div className="form-group">
                <label>Codice fiscale</label>
                <input
                  type="text"
                  value={formData.codice_fiscale}
                  onChange={(e) => setFormData({...formData, codice_fiscale: e.target.value.toUpperCase()})}
                  placeholder="RSSMRA80A01F205X"
                  maxLength={16}
                />
                <small>
                  È quello che permette di attribuire le buste paga alla persona giusta:
                  sul cedolino è stampato, e viene compilato da solo al primo caricamento.
                </small>
              </div>

              <div className="form-group">
                <label>Cellulare</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  placeholder="333 1234567"
                />
              </div>

              <div className="form-group">
                <label>Matricola</label>
                <input
                  type="text"
                  value={formData.matricola}
                  onChange={(e) => setFormData({...formData, matricola: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Data di nascita</label>
                <input
                  type="date"
                  value={formData.data_nascita}
                  onChange={(e) => setFormData({...formData, data_nascita: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Data di assunzione</label>
                <input
                  type="date"
                  value={formData.data_assunzione}
                  onChange={(e) => setFormData({...formData, data_assunzione: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.postazione}
                    onChange={(e) => setFormData({ ...formData, postazione: e.target.checked })} />
                  Postazione (tablet al banco)
                </label>
                <small>
                  Non è una persona ma un dispositivo: entra col QR code, la sessione dura mesi
                  invece di una settimana e si apre direttamente sulla pagina Banco. Ogni sua
                  richiesta viene verificata, così disattivarla ha effetto subito.
                </small>
              </div>

              <div className="form-group">
                <label>Data di cessazione</label>
                <input
                  type="date"
                  value={formData.data_cessazione}
                  onChange={(e) => setFormData({...formData, data_cessazione: e.target.value})}
                />
                <small>Da compilare solo quando il rapporto è finito.</small>
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@esempio.com"
                />
              </div>
              
              <div className="form-group">
                <label>Password {editingUser ? '(lascia vuoto per non modificare)' : '*'}</label>
                <input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder={editingUser ? 'Nuova password (opzionale)' : 'Password'}
                  minLength={6}
                />
              </div>
              
              <div className="form-group">
                <label>Ruolo *</label>
                <select
                  required
                  value={formData.ruolo}
                  onChange={(e) => setFormData({...formData, ruolo: e.target.value as any})}
                >
                  <option value="produzione">Produzione</option>
                  <option value="banconista">Banconista</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Punto Vendita</label>
                <select
                  value={formData.punto_vendita_id || ''}
                  onChange={(e) => setFormData({...formData, punto_vendita_id: e.target.value ? parseInt(e.target.value) : null})}
                >
                  <option value="">Nessun punto vendita assegnato</option>
                  {puntiVendita.filter(pv => pv.attivo).map(pv => (
                    <option key={pv.id} value={pv.id}>{pv.nome}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Costo Orario (€/h)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.costo_orario}
                  onChange={(e) => setFormData({...formData, costo_orario: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              
              {editingUser && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.attivo}
                      onChange={(e) => setFormData({...formData, attivo: e.target.checked})}
                    />
                    Utente attivo
                  </label>
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                >
                  {editingUser ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal per Badge QR */}
      {showBadgeModal && badgeUser && (
        <div className="modal-overlay">
          <div className="modal badge-modal-container">
            <div className="modal-header">
              <h2><IdCard size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />Badge Accesso Rapido</h2>
              <button 
                className="btn-close"
                onClick={() => setShowBadgeModal(false)}
                disabled={badgeLoading}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body badge-modal-body">
              {/* Contenitore Badge da stampare/esportare */}
              <div className="badge-card-wrapper">
                <div className="badge-card-container" id="operator-badge-card">
                  <div className="badge-card-header">
                    <div className="badge-card-tenant">
                      {user?.tenant_name ? user.tenant_name.toUpperCase() : 'SISTEMA GELATO'}
                    </div>
                    <div className="badge-card-access">ACCESSO RISERVATO</div>
                  </div>
                  <div className="badge-card-body">
                    {badgeLoading && !badgeQrCodeUrl ? (
                      <div className="badge-card-loader">
                        <div className="loading-spinner" style={{ width: '30px', height: '30px' }}></div>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>Generazione QR...</span>
                      </div>
                    ) : badgeQrCodeUrl ? (
                      <img src={badgeQrCodeUrl} alt="QR Code" className="badge-card-qr" />
                    ) : (
                      <div className="badge-card-error">Errore generazione QR</div>
                    )}
                  </div>
                  <div className="badge-card-footer">
                    <div className="badge-card-name">{badgeUser.nome}</div>
                    <div className="badge-card-role">{badgeUser.ruolo.toUpperCase()}</div>
                  </div>
                </div>
              </div>
              
              <div className="badge-modal-info">
                <p>Questo badge consente l'accesso rapido all'applicazione tramite scansione QR. Stampalo o salvalo sul dispositivo per darlo all'operatore.</p>
              </div>
            </div>
            
            <div className="form-actions badge-modal-actions">
              <button 
                type="button" 
                className="btn btn-warning"
                onClick={handleRegenerateBadgeQr}
                disabled={badgeLoading}
              >
                <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Rigenera QR
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleDownloadBadgePdf}
                disabled={badgeLoading || !badgeQrCodeUrl}
              >
                {badgeLoading ? 'Generazione...' : <><Download size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />Scarica Badge (PDF)</>}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowBadgeModal(false)}
                disabled={badgeLoading}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtentiPage;
